import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './Auth';
import { useNavigate, useLocation } from 'react-router-dom';
import CirclePack from './Holacracy/CirclePack';
import { Plus, GitMerge, RefreshCcw, ListChecks, BarChart3 } from 'lucide-react';
import TensionEditor from './Holacracy/TensionEditor';
import TensionsPanel from './Holacracy/TensionsPanel';
import PointsPanel from './Holacracy/PointsPanel';
import MeetingPanel from './Holacracy/MeetingPanel';
import MeetingHistoryModal from './Holacracy/MeetingHistoryModal';
import VotesPanel from './Holacracy/VotesPanel';
import StructurePanel from './Holacracy/StructurePanel';
import CircleInsightsPanel from './Holacracy/CircleInsightsPanel';
import useMeetingState from './Holacracy/hooks/useMeetingState';
import { apiService } from '../services/api';

// Inline editing helpers (one field at a time)
const sanitizeList = (list) =>
  Array.isArray(list) ? list.map((v) => (v || '').trim()).filter(Boolean) : [];

const getArrayField = (value, { stringFallback = false } = {}) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') return [];
  const trimmed = value.trim();
  if (!trimmed) return [];
  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) return parsed;
    if (stringFallback && typeof parsed === 'string' && parsed.trim()) {
      return [parsed.trim()];
    }
    return [];
  } catch {
    return stringFallback ? [trimmed] : [];
  }
};

const normalizeStringListField = (value) =>
  getArrayField(value, { stringFallback: true })
    .map((item) => (typeof item === 'string' ? item : String(item ?? '')))
    .map((item) => item.trim())
    .filter(Boolean);

const normalizeStrategyField = (value) =>
  getArrayField(value)
    .map((item) => {
      if (typeof item === 'string') {
        const content = item.trim();
        return content ? { content } : null;
      }
      return item && typeof item === 'object' ? item : null;
    })
    .filter(Boolean);

const normalizeOwnerIds = (ownerIds, ownerId) => {
  let source = [];
  if (Array.isArray(ownerIds)) {
    source = ownerIds;
  } else if (typeof ownerIds === 'string') {
    const trimmed = ownerIds.trim();
    if (trimmed) {
      try {
        const parsed = JSON.parse(trimmed);
        source = Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        source = [trimmed];
      }
    }
  }

  const ids = source
    .filter((id) => id !== null && id !== undefined && id !== '')
    .map((id) => Number(id))
    .filter((id) => !Number.isNaN(id));
  const primary = Number(ownerId);
  if (ownerId !== null && ownerId !== undefined && ownerId !== '' && !Number.isNaN(primary)) {
    ids.unshift(primary);
  }
  return Array.from(new Set(ids));
};

const normalizeCircle = (circle) => {
  if (!circle) return circle;
  const rawTimeout = Number(circle.vote_timeout_days || 0);
  const vote_timeout_days = Number.isNaN(rawTimeout) || rawTimeout < 1 ? 3 : rawTimeout;
  return {
    ...circle,
    domains: normalizeStringListField(circle.domains),
    accountabilities: normalizeStringListField(circle.accountabilities),
    strategy: normalizeStrategyField(circle.strategy),
    owner_ids: normalizeOwnerIds(circle.owner_ids, circle.owner_id),
    vote_timeout_days,
  };
};

const buildEntitySignature = (items, fields = ['id']) => {
  if (!Array.isArray(items)) return '';
  return items
    .map((item) =>
      fields.map((field) => String(item?.[field] ?? '')).join(':')
    )
    .sort()
    .join('|');
};

const focusMentionPattern = /@\[(circle|role|project):(\d+)\|([^\]]+)\]/g;

const formatFocusForExport = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.replace(focusMentionPattern, (_match, _type, _id, label) => `@${label}`);
};

const nullableText = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
};

const slugifyFilenamePart = (value, fallback = 'organisation') => {
  const slug = String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || fallback;
};

const downloadJsonFile = (payload, filename) => {
  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: 'application/json;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const HolacracyPage = () => {
  const DAILY_HOURS = 7.8;
  const [circles, setCircles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedCircle, setSelectedCircle] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [superEditMode, setSuperEditMode] = useState(false);
  const [newCircleParentId, setNewCircleParentId] = useState(null);
  const [editingField, setEditingField] = useState(null); // name | purpose | owner | domains | accountabilities | strategy | vote_timeout_days
  const [activeTab, setActiveTab] = useState('structure'); // structure | tensions | points | votes | meeting
  const [projects, setProjects] = useState([]);
  const [projectSprints, setProjectSprints] = useState({});
  const [slots, setSlots] = useState([]);
  const [tensions, setTensions] = useState([]);
  const [circlePoints, setCirclePoints] = useState([]);
  const [circlePointsLoading, setCirclePointsLoading] = useState(false);
  const [circlePointsError, setCirclePointsError] = useState(null);
  const [tensionDraft, setTensionDraft] = useState(null);
  const [selectedTensionId, setSelectedTensionId] = useState(null);
  const [tensionsLoading, setTensionsLoading] = useState(false);
  const [tensionError, setTensionError] = useState(null);
  const [votes, setVotes] = useState([]);
  const [votesLoading, setVotesLoading] = useState(false);
  const [votesError, setVotesError] = useState(null);
  const [tensionVotes, setTensionVotes] = useState([]);
  const [tensionVotesLoading, setTensionVotesLoading] = useState(false);
  const [showTensionModal, setShowTensionModal] = useState(false);
  const [roleFocusMap, setRoleFocusMap] = useState({});
  const [organizationExporting, setOrganizationExporting] = useState(false);
  const [projectMetricsById, setProjectMetricsById] = useState({});
  const [projectMetricsLoading, setProjectMetricsLoading] = useState({});
  const [projectMetricsError, setProjectMetricsError] = useState(null);
  const [metricsRange, setMetricsRange] = useState({ start: '', end: '' });
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const lowPowerDevice = React.useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    const cores = Number(navigator.hardwareConcurrency || 0);
    const memory = Number(navigator.deviceMemory || 0);
    return (
      (Number.isFinite(cores) && cores > 0 && cores <= 4) ||
      (Number.isFinite(memory) && memory > 0 && memory <= 4)
    );
  }, []);
  const projectMetricsByIdRef = useRef({});
  const projectMetricsLoadingRef = useRef({});
  const projectMetricsRequestsRef = useRef({});
  const isMountedRef = useRef(true);
  const [inlineDraft, setInlineDraft] = useState({
    name: '',
    purpose: '',
    owner_id: '',
    owner_ids: [],
    domains: [],
    accountabilities: [],
    strategy: [],
    color: '',
    vote_timeout_days: 3,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const effectiveUserId = React.useMemo(() => user?.employee_id ?? user?.id ?? null, [user]);
  const meetingIdFromQuery = React.useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return params.get('meeting');
  }, [location.search]);
  const meetingIdFromState = React.useMemo(() => {
    const stateMeetingId = location.state?.resumeMeetingId;
    if (stateMeetingId === null || stateMeetingId === undefined || stateMeetingId === '') return null;
    return String(stateMeetingId);
  }, [location.state]);
  const pendingResumeMeetingId = meetingIdFromState || meetingIdFromQuery;
  const isSuperAdmin = React.useMemo(
    () => Number(user?.employee_id ?? user?.id) === 1,
    [user]
  );
  const isAdminByTag = React.useMemo(() => {
    const employeeId = user?.employee_id;
    if (!employeeId) return Boolean(user?.is_admin);
    const employee = employees.find((emp) => Number(emp.id) === Number(employeeId));
    if (!employee) return Boolean(user?.is_admin);
    return Boolean(employee.is_admin);
  }, [employees, user]);
  const isSuperEditor = isSuperAdmin || isAdminByTag;
  const lastSelectedId = useRef(null);

  const API_URL = apiService.getBaseUrl();

  useEffect(() => {
    if (!isSuperEditor && superEditMode) {
      setSuperEditMode(false);
      setIsEditing(false);
      setEditingField(null);
    }
  }, [isSuperEditor, superEditMode]);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  useEffect(() => {
    projectMetricsByIdRef.current = projectMetricsById;
  }, [projectMetricsById]);
  useEffect(() => {
    projectMetricsLoadingRef.current = projectMetricsLoading;
  }, [projectMetricsLoading]);

  const normalizeTension = useCallback((tension) => {
    if (!tension) return tension;
    const action = tension.action || (tension.proposed_data?.delete ? 'delete' : 'edit');
    let operations = tension.operations || [];
    if (!Array.isArray(operations) && typeof operations === 'string') {
      try {
        operations = JSON.parse(operations);
      } catch (err) {
        operations = [];
      }
    }
    if (!Array.isArray(operations)) operations = [];
    return { ...tension, action, operations };
  }, []);

  const {
    activeMeeting,
    meetingTensions,
    meetingParticipants,
    meetingNotes,
    meetingPresenceCount,
    meetingConnectedUsers,
    meetingRealtimeConnected,
    meetingFormVisible,
    meetingPoints,
    meetingPreparedPoints,
    meetingPreparedPointsLoading,
    selectedMeetingPointId,
    selectedMeetingPointIdShared,
    selectedMeetingProjectId,
    selectedMeetingProjectIdShared,
    meetingProjectReviews,
    meetingPointComposer,
    meetingLoading,
    meetingError,
    meetingHistory,
    meetingHistoryLoading,
    meetingHistoryError,
    showMeetingHistory,
    meetingPhase,
    meetingDraft,
    selectedMeetingTensionId,
    selectedMeetingTensionIdShared,
    openMeetings,
    openMeetingsByCircle,
    openMeetingCircleIds,
    setSelectedMeetingTensionId,
    fetchMeetingTensions,
    fetchMeetingProjectReviews,
    sendMeetingRealtime,
    handleMeetingDraftChange,
    handleTransferSecretary,
    handleMeetingNotesChange,
    handleOpenMeeting,
    handleCloseMeeting,
    handleOpenMeetingHistory,
    handleCloseMeetingHistory,
    handleImportMeetingTension,
    handleCreateMeetingTension,
    handleJoinMeeting,
    handleToggleMeetingForm,
    handleAddMeetingPoint,
    handleImportMeetingPoint,
    handleSelectMeetingPoint,
    handleMeetingPointDecision,
    handleMeetingPointToTension,
    handleMeetingPointNotesChange,
    handleMeetingPointComposerUpdate,
    handleSelectMeetingProject,
    handleSelectMeetingPhase,
    handleUpdateMeetingProjectReview,
    handleCreateTacticalTask,
    handleCreateTacticalObjective,
    handleInviteMeetingParticipant,
    handleLiveMeetingTensionEdit,
    handleMeetingDecision,
  } = useMeetingState({
    apiUrl: API_URL,
    getAuthHeaders,
    user,
    selectedCircle,
    setSelectedCircle,
    activeTab,
    setActiveTab,
    circles,
    normalizeTension,
  });

  const fetchCircles = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/circles`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch circles');
      const data = await response.json();
      const normalized = (data || []).map(normalizeCircle);
      setCircles(normalized);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [API_URL, getAuthHeaders]);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/employees`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch employees');
      const data = await response.json();
      setEmployees(data);
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  }, [API_URL, getAuthHeaders]);

  const fetchProjects = useCallback(async () => {
    try {
      let response = await fetch(`${API_URL}/projects/options`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        response = await fetch(`${API_URL}/projects`, {
          headers: getAuthHeaders(),
        });
      }
      if (!response.ok) throw new Error('Failed to fetch projects');
      const data = await response.json();
      const next = Array.isArray(data) ? data : [];
      setProjects((prev) => {
        const prevSig = buildEntitySignature(prev, ['id', 'name', 'project_number', 'status', 'circle_id']);
        const nextSig = buildEntitySignature(next, ['id', 'name', 'project_number', 'status', 'circle_id']);
        return prevSig === nextSig ? prev : next;
      });
    } catch (err) {
      console.error('Error fetching projects:', err);
    }
  }, [API_URL, getAuthHeaders]);

  const fetchSlots = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/slots`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch slots');
      const data = await response.json();
      const next = Array.isArray(data) ? data : [];
      setSlots((prev) => {
        const prevSig = buildEntitySignature(prev, ['id', 'name', 'start_date', 'end_date']);
        const nextSig = buildEntitySignature(next, ['id', 'name', 'start_date', 'end_date']);
        return prevSig === nextSig ? prev : next;
      });
    } catch (err) {
      console.error('Error fetching slots:', err);
      setSlots([]);
    }
  }, [API_URL, getAuthHeaders]);

  const fetchProjectMetrics = useCallback(
    async (projectId) => {
      if (!projectId) return [];
      const key = String(projectId);
      if (projectMetricsRequestsRef.current[key]) {
        return projectMetricsRequestsRef.current[key];
      }
      const requestPromise = (async () => {
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), 30000);
        try {
          const response = await fetch(`${API_URL}/projects/${projectId}/metrics`, {
            headers: getAuthHeaders(),
            signal: controller.signal,
          });
          if (!response.ok) throw new Error('Failed to fetch project metrics');
          const data = await response.json();
          return Array.isArray(data) ? data : [];
        } catch (err) {
          if (err?.name === 'AbortError') {
            throw new Error('Timeout while fetching project metrics');
          }
          throw err;
        } finally {
          window.clearTimeout(timeoutId);
        }
      })();
      projectMetricsRequestsRef.current[key] = requestPromise;
      try {
        return await requestPromise;
      } finally {
        if (projectMetricsRequestsRef.current[key] === requestPromise) {
          delete projectMetricsRequestsRef.current[key];
        }
      }
    },
    [API_URL, getAuthHeaders]
  );

  const fetchProjectObjectives = useCallback(
    async (projectId) => {
      if (!projectId) return [];
      const response = await fetch(`${API_URL}/projects/${projectId}/objectives`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch project objectives');
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    },
    [API_URL, getAuthHeaders]
  );

  const buildFocusKey = (circleId, employeeId) => `${circleId}:${employeeId}`;

  const fetchRoleFocus = useCallback(
    async (parentId) => {
      if (!parentId) {
        setRoleFocusMap({});
        return;
      }
      try {
        const response = await fetch(`${API_URL}/circle-focus?parent_id=${parentId}`, {
          headers: getAuthHeaders(),
        });
        if (!response.ok) throw new Error('Failed to fetch role focus');
        const data = await response.json();
        const next = {};
        (data || []).forEach((item) => {
          next[buildFocusKey(item.circle_id, item.employee_id)] = item.focus_text || '';
        });
        setRoleFocusMap(next);
      } catch (err) {
        console.error('Error fetching role focus:', err);
      }
    },
    [API_URL, getAuthHeaders]
  );

  const fetchProjectSprints = useCallback(
    async (projectId) => {
      if (!projectId) return;
      const key = String(projectId);
      if (projectSprints[key]) return;
      try {
        const response = await fetch(`${API_URL}/projects/${projectId}/sprints`, {
          headers: getAuthHeaders(),
        });
        if (!response.ok) throw new Error('Failed to fetch sprints');
        const data = await response.json();
        setProjectSprints((prev) => ({ ...prev, [key]: data || [] }));
      } catch (err) {
        console.error('Error fetching sprints:', err);
      }
    },
    [API_URL, getAuthHeaders, projectSprints]
  );


  const fetchTensions = useCallback(async () => {
    try {
      setTensionsLoading(true);
      const response = await fetch(`${API_URL}/circle-tensions`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Impossible de recuperer les tensions');
      const data = await response.json();
      const normalized = (data || []).map(normalizeTension);
      setTensions(normalized);
      const historyStatuses = ['applied', 'rejected', 'meeting'];
      const active = normalized.filter((t) => !historyStatuses.includes((t.status || '').toLowerCase()));
      if (selectedTensionId && !active.some((t) => t.id === selectedTensionId)) {
        setSelectedTensionId(null);
        setTensionDraft(null);
      }
      setTensionError(null);
    } catch (err) {
      setTensionError(err.message);
    } finally {
      setTensionsLoading(false);
    }
  }, [API_URL, getAuthHeaders, selectedTensionId]);

  const fetchCirclePoints = useCallback(async (circleIdOverride) => {
    const circleId = circleIdOverride || selectedCircle?.id;
    if (!circleId) {
      setCirclePoints([]);
      return;
    }
    try {
      setCirclePointsLoading(true);
      const response = await fetch(`${API_URL}/circle-points?circle_id=${circleId}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Impossible de recuperer les points');
      const data = await response.json();
      setCirclePoints(Array.isArray(data) ? data : []);
      setCirclePointsError(null);
    } catch (err) {
      setCirclePointsError(err.message);
      setCirclePoints([]);
    } finally {
      setCirclePointsLoading(false);
    }
  }, [API_URL, getAuthHeaders, selectedCircle?.id]);

  const handleCreateCirclePoint = useCallback(async (title) => {
    const trimmed = (title || '').trim();
    if (!trimmed || !selectedCircle?.id) return null;
    try {
      const response = await fetch(`${API_URL}/circle-points`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ circle_id: selectedCircle.id, title: trimmed }),
      });
      if (!response.ok) throw new Error("Impossible d'ajouter le point");
      const created = await response.json();
      fetchCirclePoints(selectedCircle.id);
      return created;
    } catch (err) {
      setCirclePointsError(err.message);
      return null;
    }
  }, [API_URL, getAuthHeaders, selectedCircle?.id, fetchCirclePoints]);

  const handleDeleteCirclePoint = useCallback(async (pointId) => {
    if (!pointId) return;
    if (!window.confirm('Supprimer ce point ?')) return;
    try {
      const response = await fetch(`${API_URL}/circle-points/${pointId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Suppression impossible');
      fetchCirclePoints(selectedCircle?.id);
    } catch (err) {
      setCirclePointsError(err.message);
    }
  }, [API_URL, getAuthHeaders, selectedCircle?.id, fetchCirclePoints]);

  const fetchTensionVotes = useCallback(
    async (tensionId) => {
      if (!tensionId) {
        setTensionVotes([]);
        return;
      }
      try {
        setTensionVotesLoading(true);
        const response = await fetch(`${API_URL}/circle-votes/tension/${tensionId}`, {
          headers: getAuthHeaders(),
        });
        if (!response.ok) throw new Error('Impossible de recuperer les votes');
        const data = await response.json();
        setTensionVotes(data || []);
      } catch (err) {
        setTensionVotes([]);
      } finally {
        setTensionVotesLoading(false);
      }
    },
    [API_URL, getAuthHeaders]
  );

  useEffect(() => {
    if (tensionDraft?.id && tensionDraft.status === 'proposal') {
      fetchTensionVotes(tensionDraft.id);
    } else {
      setTensionVotes([]);
    }
  }, [tensionDraft?.id, tensionDraft?.status, fetchTensionVotes]);

  const fetchVotes = useCallback(async (options = {}) => {
    const { silent = false } = options;
    try {
      if (!silent) setVotesLoading(true);
      const response = await fetch(`${API_URL}/circle-votes/pending`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Impossible de recuperer les votes');
      const data = await response.json();
      const nextVotes = Array.isArray(data) ? data : [];
      setVotes((prev) => {
        const prevSig = buildEntitySignature(prev, ['id', 'status', 'updated_at', 'created_at']);
        const nextSig = buildEntitySignature(nextVotes, ['id', 'status', 'updated_at', 'created_at']);
        return prevSig === nextSig ? prev : nextVotes;
      });
      setVotesError(null);
    } catch (err) {
      setVotesError(err.message);
    } finally {
      if (!silent) setVotesLoading(false);
    }
  }, [API_URL, getAuthHeaders]);

  useEffect(() => {
    fetchCircles();
    fetchEmployees();
    let timeoutId = null;
    let idleId = null;
    const fetchSupportData = () => {
      fetchProjects();
      fetchSlots();
    };
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(fetchSupportData, { timeout: 1200 });
    } else {
      timeoutId = window.setTimeout(fetchSupportData, 120);
    }
    return () => {
      if (idleId !== null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [fetchCircles, fetchEmployees, fetchProjects, fetchSlots]);

  useEffect(() => {
    if (activeTab === 'tensions' || activeTab === 'meeting') {
      fetchTensions();
    }
    if (activeTab === 'points') {
      fetchCirclePoints();
    }
    if (activeTab === 'votes') {
      fetchVotes();
    }
  }, [activeTab, fetchTensions, fetchCirclePoints, fetchVotes]);

  useEffect(() => {
    let active = true;
    const refreshVotes = async () => {
      if (!active) return;
      await fetchVotes({ silent: true });
    };
    refreshVotes();
    const interval = setInterval(refreshVotes, 20000);
    const handleFocus = () => refreshVotes();
    window.addEventListener('focus', handleFocus);
    return () => {
      active = false;
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchVotes]);

  const childrenByParent = React.useMemo(() => {
    const map = {};
    circles.forEach((c) => {
      if (!c.parent_id) return;
      if (!map[c.parent_id]) map[c.parent_id] = [];
      map[c.parent_id].push(c);
    });
    return map;
  }, [circles]);

  const handleExportOrganization = useCallback(async () => {
    if (!Array.isArray(circles) || circles.length === 0) {
      alert('Aucune organisation a exporter.');
      return;
    }
    setOrganizationExporting(true);
    try {
      const response = await fetch(`${API_URL}/circle-focus`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Impossible de recuperer les focus des roles');
      const focusRows = await response.json();
      const focusByKey = {};
      (focusRows || []).forEach((item) => {
        if (!item?.circle_id || !item?.employee_id) return;
        focusByKey[buildFocusKey(item.circle_id, item.employee_id)] = item.focus_text || '';
      });

      let employeeList = employees || [];
      if (employeeList.length === 0) {
        const employeeResponse = await fetch(`${API_URL}/employees`, {
          headers: getAuthHeaders(),
        });
        if (employeeResponse.ok) {
          const employeeData = await employeeResponse.json();
          employeeList = Array.isArray(employeeData) ? employeeData : [];
        }
      }
      const employeeById = new Map();
      employeeList.forEach((employee) => {
        if (employee?.id !== undefined && employee?.id !== null) {
          employeeById.set(Number(employee.id), employee);
        }
      });
      const circleById = new Map((circles || []).map((circle) => [Number(circle.id), circle]));
      const buildLeaders = (circle) =>
        normalizeOwnerIds(circle?.owner_ids, circle?.owner_id).map((id) => {
          const employee = employeeById.get(Number(id));
          return {
            id,
            nom: employee?.name || null,
            email: employee?.email || null,
            focus: formatFocusForExport(focusByKey[buildFocusKey(circle.id, id)]),
          };
        });

      const buildNode = (circle) => {
        const children = childrenByParent[circle.id] || [];
        const isRole = Boolean(circle.parent_id) && children.length === 0;
        return {
          id: circle.id,
          type: isRole ? 'role' : 'cercle',
          nom: nullableText(circle.name),
          vision: nullableText(circle.vision),
          raison_etre: nullableText(circle.purpose),
          redevabilites: sanitizeList(circle.accountabilities),
          leaders: buildLeaders(circle),
          enfants: children.map(buildNode),
        };
      };

      const roots = (circles || []).filter(
        (circle) => !circle.parent_id || !circleById.has(Number(circle.parent_id))
      );
      const rootNode =
        roots.length === 1
          ? buildNode(roots[0])
          : {
              id: null,
              type: 'organisation',
              nom: 'Organization',
              vision: null,
              raison_etre: null,
              redevabilites: [],
              leaders: [],
              enfants: roots.map(buildNode),
            };
      const exportPayload = {
        format: 'holacracy-organization-tree-v1',
        exported_at: new Date().toISOString(),
        organisation: rootNode,
      };
      const filenameRoot = slugifyFilenamePart(rootNode.nom, 'organisation');
      const datePart = new Date().toISOString().slice(0, 10);
      downloadJsonFile(exportPayload, `holacratie_${filenameRoot}_${datePart}.json`);
    } catch (err) {
      alert(err?.message || 'Erreur lors de l export JSON.');
    } finally {
      setOrganizationExporting(false);
    }
  }, [API_URL, childrenByParent, circles, employees, getAuthHeaders]);

  const parseDateInput = useCallback((value) => {
    if (!value) return null;
    if (value instanceof Date) return new Date(value);
    if (typeof value === 'string') {
      const trimmed = value.split('T')[0];
      const [year, month, day] = trimmed.split('-').map(Number);
      if (year && month && day) {
        return new Date(year, month - 1, day);
      }
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed;
  }, []);

  const formatDateInput = useCallback(
    (value) => {
      const parsed = parseDateInput(value);
      if (!parsed) return '';
      return parsed.toLocaleDateString('sv-SE');
    },
    [parseDateInput]
  );

  const currentSlot = React.useMemo(() => {
    if (!Array.isArray(slots) || slots.length === 0) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return (
      slots.find((slot) => {
        const start = parseDateInput(slot.start_date);
        const end = parseDateInput(slot.end_date || slot.start_date);
        if (!start || !end) return false;
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        return today >= start && today <= end;
      }) || null
    );
  }, [slots, parseDateInput]);

  const currentSlotRange = React.useMemo(() => {
    if (!currentSlot) return null;
    const start = formatDateInput(currentSlot.start_date);
    const end = formatDateInput(currentSlot.end_date || currentSlot.start_date);
    if (!start || !end) return null;
    return {
      start,
      end,
      label: currentSlot.name || `Slot ${currentSlot.id || ''}`.trim(),
    };
  }, [currentSlot, formatDateInput]);

  useEffect(() => {
    if (!currentSlotRange) return;
    setMetricsRange((prev) => {
      if (prev.start || prev.end) return prev;
      return { start: currentSlotRange.start, end: currentSlotRange.end };
    });
  }, [currentSlotRange]);

  const hierarchyData = React.useMemo(() => {
    const buildHierarchy = (flatList) => {
      if (!flatList || flatList.length === 0) return null;
      const map = {};
      const roots = [];
      flatList.forEach((item) => {
        map[item.id] = { ...item, children: [] };
      });
      flatList.forEach((item) => {
        if (item.parent_id && map[item.parent_id]) {
          map[item.parent_id].children.push(map[item.id]);
        } else {
          roots.push(map[item.id]);
        }
      });
      if (roots.length > 1) return { name: 'Organization', children: roots };
      return roots[0] || { name: 'Organization', children: [] };
    };
    return buildHierarchy(circles);
  }, [circles]);

  const circleMemberIds = React.useMemo(() => {
    if (!selectedCircle?.id) return [];
    const ids = new Set();
    const addId = (val) => {
      if (val === null || val === undefined || val === '') return;
      const num = Number(val);
      if (!Number.isNaN(num)) ids.add(num);
    };
    (selectedCircle.owner_ids || []).forEach(addId);
    addId(selectedCircle.owner_id);
    const directRoles = childrenByParent[selectedCircle.id] || [];
    directRoles.forEach((role) => {
      (role.owner_ids || []).forEach(addId);
      addId(role.owner_id);
    });
    return Array.from(ids);
  }, [selectedCircle, childrenByParent]);

  useEffect(() => {
    if (!selectedCircle && !pendingResumeMeetingId && hierarchyData && !loading && !isEditing) {
      if (hierarchyData.id) {
        setSelectedCircle(hierarchyData);
      } else if (hierarchyData.children && hierarchyData.children.length > 0) {
        setSelectedCircle(hierarchyData.children[0]);
      }
    }
  }, [pendingResumeMeetingId, hierarchyData, loading, selectedCircle, isEditing]);

  useEffect(() => {
    if (!selectedCircle?.id || isEditing || editingField) return;
    const latest = circles.find((circle) => String(circle?.id) === String(selectedCircle.id));
    if (!latest) return;
    setSelectedCircle((prev) => {
      if (!prev?.id || String(prev.id) !== String(latest.id)) return prev;
      return latest;
    });
  }, [circles, editingField, isEditing, selectedCircle?.id]);

  useEffect(() => {
    if (!pendingResumeMeetingId) return;
    if (!Array.isArray(openMeetings) || openMeetings.length === 0) return;
    const meeting = openMeetings.find((item) => String(item?.id) === String(pendingResumeMeetingId));
    if (!meeting) return;
    if (String(activeMeeting?.id) === String(meeting.id) && activeTab === 'meeting') {
      if (meetingIdFromQuery) {
        navigate('/holacracy', { replace: true });
      }
      return;
    }
    const targetCircle = circles.find((circle) => String(circle?.id) === String(meeting?.circle_id));
    if (!targetCircle) return;
    handleJoinMeeting(meeting);
    if (meetingIdFromQuery) {
      navigate('/holacracy', { replace: true });
    }
  }, [pendingResumeMeetingId, meetingIdFromQuery, openMeetings, circles, activeMeeting?.id, activeTab, handleJoinMeeting, navigate]);

  const handleCircleClick = useCallback((circle) => {
    // Allow selecting virtual root (no id) to handle consolidation
    const latest = circle?.id
      ? circles.find((item) => String(item?.id) === String(circle.id))
      : null;
    setSelectedCircle(latest || normalizeCircle(circle));
    setIsEditing(false);
    setNewCircleParentId(null);
    setEditingField(null);
    setSelectedEmployeeId(null);
  }, [circles]);

  const handleSelectEmployee = useCallback((employeeId) => {
    if (employeeId === null || employeeId === undefined || employeeId === '') return;
    const normalized = Number(employeeId);
    const nextId = Number.isNaN(normalized) ? employeeId : normalized;
    setSelectedEmployeeId((prev) => (String(prev) === String(nextId) ? null : nextId));
    setIsEditing(false);
    setEditingField(null);
  }, []);

  useEffect(() => {
    if (!selectedCircle) return;
    setInlineDraft({
      name: selectedCircle.name || '',
      purpose: selectedCircle.purpose || '',
      owner_id: selectedCircle.owner_id || '',
      owner_ids: selectedCircle.owner_ids || (selectedCircle.owner_id ? [selectedCircle.owner_id] : []),
      domains: selectedCircle.domains || [],
      accountabilities: selectedCircle.accountabilities || [],
      strategy: selectedCircle.strategy || [],
      color: selectedCircle.color || '',
      vote_timeout_days: selectedCircle.vote_timeout_days || 3,
    });
    if (lastSelectedId.current !== selectedCircle.id) {
      setEditingField(null);
      lastSelectedId.current = selectedCircle.id;
    }
  }, [selectedCircle]);

  const resolveFocusParentId = useCallback(
    (circle) => {
      if (!circle?.id) return null;
      const hasChildren = (childrenByParent[circle.id] || []).length > 0;
      const isRoleCircleLocal = Boolean(circle.parent_id) && !hasChildren;
      return isRoleCircleLocal ? (circle.parent_id || null) : circle.id;
    },
    [childrenByParent]
  );

  const handleSaveRoleFocus = useCallback(
    async (circleId, employeeId, focusText) => {
      const trimmed = typeof focusText === 'string' ? focusText.trim() : '';
      const key = buildFocusKey(circleId, employeeId);
      setRoleFocusMap((prev) => {
        const next = { ...prev };
        if (trimmed) {
          next[key] = focusText;
        } else {
          delete next[key];
        }
        return next;
      });
      try {
        const response = await fetch(`${API_URL}/circle-focus`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({
            circle_id: circleId,
            employee_id: employeeId,
            focus_text: focusText,
          }),
        });
        if (!response.ok) throw new Error('Failed to save role focus');
      } catch (err) {
        console.error('Error saving role focus:', err);
        const parentId = resolveFocusParentId(selectedCircle);
        if (parentId) {
          fetchRoleFocus(parentId);
        }
      }
    },
    [API_URL, getAuthHeaders, fetchRoleFocus, resolveFocusParentId, selectedCircle]
  );

  useEffect(() => {
    const parentId = resolveFocusParentId(selectedCircle);
    if (!selectedCircle?.id || !parentId) {
      setRoleFocusMap({});
      return;
    }
    fetchRoleFocus(parentId);
  }, [selectedCircle, resolveFocusParentId, fetchRoleFocus]);

  const updateInlineField = (key, value) => {
    setInlineDraft((prev) => ({ ...prev, [key]: value }));
  };

  const updateInlineListItem = (listName, index, value) => {
    const copy = [...(inlineDraft[listName] || [])];
    copy[index] = value;
    const next = { ...inlineDraft, [listName]: copy };
    commitInline(next);
  };

  const addInlineItem = (listName, value) => {
    const trimmed = value?.trim?.() || '';
    if (!trimmed) return;
    const next = { ...inlineDraft, [listName]: [...(inlineDraft[listName] || []), trimmed] };
    commitInline(next);
  };

  const removeInlineItem = (listName, index) => {
    const copy = [...(inlineDraft[listName] || [])];
    copy.splice(index, 1);
    const next = { ...inlineDraft, [listName]: copy };
    commitInline(next);
  };

  const updateInlineStrategyItem = (index, content) => {
    const copy = [...(inlineDraft.strategy || [])];
    copy[index] = { ...copy[index], content, updated_at: new Date().toISOString() };
    const next = { ...inlineDraft, strategy: copy };
    commitInline(next);
  };

  const addInlineStrategyItem = (content) => {
    const trimmed = content?.trim?.() || '';
    if (!trimmed) return;
    const newItem = { content: trimmed, updated_at: new Date().toISOString() };
    const next = { ...inlineDraft, strategy: [...(inlineDraft.strategy || []), newItem] };
    commitInline(next);
  };

  const removeInlineStrategyItem = (index) => {
    const copy = [...(inlineDraft.strategy || [])];
    copy.splice(index, 1);
    const next = { ...inlineDraft, strategy: copy };
    commitInline(next);
  };

  const handleSave = async (formData) => {
    try {
      const method = selectedCircle && selectedCircle.id ? 'PUT' : 'POST';
      const url = selectedCircle && selectedCircle.id ? `${API_URL}/circles/${selectedCircle.id}` : `${API_URL}/circles`;
      const hasChildren = selectedCircle?.id ? (childrenByParent[selectedCircle.id]?.length > 0) : false;
      const isRole = selectedCircle?.id ? (!!selectedCircle.parent_id && !hasChildren) : Boolean(formData.parent_id);
      const rawOwnerIds = Array.isArray(formData.owner_ids) ? formData.owner_ids : [];
      let ownerIds = rawOwnerIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id));
      let ownerIdValue = formData.owner_id === '' || formData.owner_id === undefined || formData.owner_id === null
        ? null
        : Number(formData.owner_id);

      if (ownerIdValue !== null && !Number.isNaN(ownerIdValue) && !ownerIds.includes(ownerIdValue)) {
        ownerIds = [ownerIdValue, ...ownerIds];
      }
      if (!isRole && ownerIds.length > 1) {
        ownerIds = [ownerIds[0]];
      }
      if (!isRole) {
        ownerIdValue = ownerIds[0] ?? null;
      }

      const dataToSend = {
        ...formData,
        owner_id: ownerIdValue,
        owner_ids: ownerIds,
        domains: sanitizeList(formData.domains),
        accountabilities: sanitizeList(formData.accountabilities),
        strategy: (formData.strategy || []).filter((s) => s.content && s.content.trim()),
        color: (() => {
          if (formData.color === null || formData.color === undefined) return null;
          const trimmed = typeof formData.color === 'string' ? formData.color.trim() : formData.color;
          return trimmed || null;
        })(),
        vote_timeout_days: (() => {
          const raw = formData.vote_timeout_days;
          const fallback = selectedCircle?.vote_timeout_days ?? 3;
          if (raw === '' || raw === null || raw === undefined) return fallback;
          const parsed = Number(raw);
          if (Number.isNaN(parsed)) return fallback;
          return parsed < 1 ? 1 : parsed;
        })(),
      };
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(dataToSend),
      });
      if (!response.ok) throw new Error('Failed to save circle');
      let saved = null;
      try {
        saved = await response.json();
      } catch (err) {
        saved = null;
      }
      if (saved) {
        const normalizedSaved = normalizeCircle(saved);
        setSelectedCircle(normalizedSaved);
        setInlineDraft({
          name: normalizedSaved.name || '',
          purpose: normalizedSaved.purpose || '',
          owner_id: normalizedSaved.owner_id || '',
          owner_ids: normalizedSaved.owner_ids || [],
          domains: normalizedSaved.domains || [],
          accountabilities: normalizedSaved.accountabilities || [],
          strategy: normalizedSaved.strategy || [],
          color: normalizedSaved.color || '',
          vote_timeout_days: normalizedSaved.vote_timeout_days || 3,
        });
      }
      await fetchCircles();
      setIsEditing(false);
      setNewCircleParentId(null);
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const commitInline = async (nextDraft, exitAfter = false) => {
    setInlineDraft(nextDraft);
    // Optimistic local update to avoid refresh need
    if (selectedCircle?.id) {
      setSelectedCircle((prev) => ({ ...(prev || {}), ...nextDraft, id: prev.id }));
    }
    await handleSave(nextDraft);
    if (exitAfter) setEditingField(null);
  };

  const handleInlineCancel = () => {
    if (!selectedCircle) return;
    setInlineDraft({
      name: selectedCircle.name || '',
      purpose: selectedCircle.purpose || '',
      owner_id: selectedCircle.owner_id || '',
      owner_ids: selectedCircle.owner_ids || (selectedCircle.owner_id ? [selectedCircle.owner_id] : []),
      domains: selectedCircle.domains || [],
      accountabilities: selectedCircle.accountabilities || [],
      strategy: selectedCircle.strategy || [],
      color: selectedCircle.color || '',
      vote_timeout_days: selectedCircle.vote_timeout_days || 3,
    });
    setEditingField(null);
  };

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditingField(null);
    if (!selectedCircle && newCircleParentId) {
      const parentCircle = circles.find((circle) => circle.id === newCircleParentId) || null;
      setSelectedCircle(parentCircle);
    }
    setNewCircleParentId(null);
  }, [selectedCircle, newCircleParentId, circles]);

  const handleDelete = async (id) => {
    if (!window.confirm("Etes-vous sur de vouloir supprimer ce cercle et tous ses sous-cercles ?")) return;
    try {
      const response = await fetch(`${API_URL}/circles/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to delete circle');
      await fetchCircles();
      setSelectedCircle(null);
      setIsEditing(false);
      setEditingField(null);
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleInitializeRoot = async () => {
    try {
      const currentUserId = effectiveUserId ? Number(effectiveUserId) : null;
      const ownerIds = currentUserId !== null && !Number.isNaN(currentUserId) ? [currentUserId] : [];
      const rootCircle = {
        name: 'Ancrage',
        purpose: 'Raison d\'etre de l\'organisation',
        domains: [],
        accountabilities: [],
        strategy: [],
        owner_id: ownerIds[0] ?? null,
        owner_ids: ownerIds,
        parent_id: null,
      };

      const response = await fetch(`${API_URL}/circles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(rootCircle),
      });

      if (!response.ok) throw new Error('Failed to create root circle');

      const saved = await response.json();
      await fetchCircles();
      setSelectedCircle(normalizeCircle(saved));
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleConsolidateRoots = async () => {
    if (!selectedCircle || selectedCircle.id) return;

    try {
      // 1. Create the new real root
      const rootCircle = {
        name: 'Organization',
        purpose: 'Structure consolidee',
        domains: [],
        accountabilities: [],
        strategy: [],
        owner_id: null,
        owner_ids: [],
        parent_id: null,
      };

      const createResponse = await fetch(`${API_URL}/circles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(rootCircle),
      });

      if (!createResponse.ok) throw new Error('Failed to create root circle');
      const newRoot = await createResponse.json();

      // 2. Update all children (current roots) to have this new root as parent
      const children = selectedCircle.children || [];
      await Promise.all(children.map(async (child) => {
        const updateResponse = await fetch(`${API_URL}/circles/${child.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({ ...child, parent_id: newRoot.id }),
        });
        if (!updateResponse.ok) console.error(`Failed to move circle ${child.name}`);
      }));

      await fetchCircles();
      setSelectedCircle(normalizeCircle(newRoot));
    } catch (err) {
      alert(`Error consolidating roots: ${err.message}`);
    }
  };

  const handleAddSubCircle = () => {
    if (selectedCircle) {
      setNewCircleParentId(selectedCircle.id);
      setSelectedCircle(null);
      setIsEditing(true);
    }
  };

  const handleToggleSuperEdit = useCallback(() => {
    if (!isSuperEditor) return;
    setSuperEditMode((prev) => {
      const next = !prev;
      if (!next) {
        setIsEditing(false);
        setEditingField(null);
      }
      return next;
    });
  }, [isSuperEditor]);

  const handleStartSuperEdit = useCallback(() => {
    if (!selectedCircle?.id) return;
    setNewCircleParentId(null);
    setEditingField(null);
    setIsEditing(true);
  }, [selectedCircle]);

  // --- Gestion des tensions ---
  const buildSnapshotFromCircle = (circle) => ({
    name: circle?.name || '',
    purpose: circle?.purpose || '',
    domains: circle?.domains || [],
    accountabilities: circle?.accountabilities || [],
    parent_id: circle?.parent_id || null,
  });

  const buildTensionTitleChanges = (draft) => {
    const ops = draft?.operations || [];
    const circlesById = new Map((circles || []).map((c) => [String(c.id), c]));
    const opLines = ops.length > 0
      ? ops.map((op) => {
        const actionLabel = op.action === 'create'
          ? 'Creation role'
          : op.action === 'delete'
            ? 'Suppression role'
            : 'Modification role';
        const roleName =
          op.data?.name ||
          op.original_data?.name ||
          circlesById.get(String(op.target_circle_id || op.circle_id))?.name ||
          'role';
        const changes = [];
        if (op.action === 'edit' && op.original_data && op.data) {
          if (op.data.name !== undefined && op.data.name !== op.original_data.name) changes.push('nom');
          if (op.data.purpose !== undefined && op.data.purpose !== op.original_data.purpose) changes.push('raison');
          if (Array.isArray(op.data.domains) && JSON.stringify(op.data.domains) !== JSON.stringify(op.original_data.domains || [])) {
            changes.push('domaines');
          }
          if (Array.isArray(op.data.accountabilities) && JSON.stringify(op.data.accountabilities) !== JSON.stringify(op.original_data.accountabilities || [])) {
            changes.push('redevabilites');
          }
        }
        if (op.action === 'create') {
          const parentName = circlesById.get(String(op.data?.parent_id || op.circle_id))?.name;
          if (parentName) changes.push(`parent ${parentName}`);
        }
        const changeLabel = changes.length > 0 ? ` (${changes.join(', ')})` : '';
        return `- ${actionLabel}: ${roleName}${changeLabel}`;
      }).join('\n')
      : `- Modification role: ${draft?.original_data?.name || 'role'}`;

    return opLines;
  };

  const normalizeTensionTitle = (raw) => {
    if (!raw) return '';
    const cleaned = String(raw)
      .replace(/[\r\n]+/g, ' ')
      .replace(/^\"|\"$/g, '')
      .replace(/^'|'$/g, '')
      .trim();
    if (!cleaned) return '';
    const words = cleaned.split(/\s+/);
    return words.slice(0, 5).join(' ');
  };

  const generateTensionTitle = async (draft) => {
    const changes = buildTensionTitleChanges(draft);
    const data = await apiService.suggestCircleTensionTitle(changes);
    return normalizeTensionTitle(data?.suggested_title);
  };

  const handleStartTension = (circleToUse = selectedCircle, action = 'edit') => {
    let original = {};
    let proposed = {};
    let operations = [];

    if (circleToUse?.id) {
      const hasChildren = (childrenByParent[circleToUse.id] || []).length > 0;
      const isRoleTarget = Boolean(circleToUse.parent_id) && !hasChildren;
      if (isRoleTarget) {
        setTensionError("Selectionnez un cercle (pas un role) pour creer une tension.");
        return;
      }
    }

    if (action === 'create') {
      original = {};
      proposed = {
        name: '',
        purpose: '',
        domains: [],
        accountabilities: [],
        parent_id: circleToUse?.id || null,
      };
      operations = [
        {
          action: 'create',
          circle_id: circleToUse?.id || null,
          target_circle_id: null,
          data: proposed,
          original_data: original,
        },
      ];
    } else {
      if (!circleToUse?.id) {
        setTensionError("Selectionnez un cercle pour creer une tension.");
        return;
      }
      original = buildSnapshotFromCircle(circleToUse);
      proposed = { ...original };
      operations = [
        {
          action: action || 'edit',
          circle_id: circleToUse.id,
          target_circle_id: circleToUse.id,
          data: proposed,
          original_data: original,
        },
      ];
    }

    setTensionDraft({
      id: null,
      circle_id: action === 'create' ? circleToUse?.id || null : circleToUse?.id,
      title: '',
      summary: '',
      status: 'draft',
      expressed_by_role_id: null,
      action,
      original_data: original,
      proposed_data: proposed,
      operations,
    });
    setSelectedTensionId(null);
    setShowTensionModal(true);
  };

  const handleSelectTension = (tension) => {
    setSelectedTensionId(tension.id);
    setTensionDraft(normalizeTension(tension));
    setActiveTab('tensions');
    setShowTensionModal(false);
    if ((tension.status === 'proposal' || tension.status === 'applied') && tension.id) {
      fetchTensionVotes(tension.id);
    } else {
      setTensionVotes([]);
    }
  };

  const handleCancelTension = () => {
    setTensionDraft(null);
    setSelectedTensionId(null);
    setShowTensionModal(false);
  };

  const updateTensionField = (key, value) => {
    setTensionDraft((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const updateTensionProposedField = (key, value) => {
    setTensionDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        proposed_data: {
          ...(prev.proposed_data || {}),
          [key]: value,
        },
      };
    });
  };

  const updateTensionList = (listName, updater) => {
    setTensionDraft((prev) => {
      if (!prev) return prev;
      const currentList = Array.isArray(prev.proposed_data?.[listName]) ? prev.proposed_data[listName] : [];
      const nextList = updater(currentList);
      return {
        ...prev,
        proposed_data: {
          ...(prev.proposed_data || {}),
          [listName]: nextList,
        },
      };
    });
  };

  const isTensionFieldChanged = (field) => {
    if (!tensionDraft) return false;
    const original = tensionDraft.original_data || {};
    const proposed = tensionDraft.proposed_data || {};
    if (Array.isArray(original[field]) || Array.isArray(proposed[field])) {
      return JSON.stringify(original[field] || []) !== JSON.stringify(proposed[field] || []);
    }
    return (proposed[field] || '') !== (original[field] || '');
  };

  const handleTensionActionChange = (nextAction) => {
    setTensionDraft((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        action: nextAction,
        proposed_data: {
          ...(prev.proposed_data || {}),
          delete: nextAction === 'delete' ? true : undefined,
        },
      };
    });
  };

  const handleSaveTension = async () => {
    if (!tensionDraft) return;
    const userTitle = tensionDraft.title?.trim();
    const fallbackName =
      circles.find((c) => String(c.id) === String(tensionDraft.circle_id))?.name ||
      tensionDraft.original_data?.name ||
      'role';
    const fallbackTitle = userTitle || `Tension ${fallbackName}`;
    const shouldAutoTitle = !userTitle;
    if (!tensionDraft.operations || tensionDraft.operations.length === 0) {
      alert('Ajoutez au moins une operation (creer/modifier/supprimer un role).');
      return;
    }
    if (shouldAutoTitle) {
      setTensionDraft((prev) => (prev ? { ...prev, title: fallbackTitle } : prev));
      setShowTensionModal(false);
    }
    try {
      setTensionsLoading(true);
      const operations = tensionDraft.operations || [];
      const firstOp = operations[0];
      const isCreateAction = firstOp?.action === 'create' || tensionDraft.action === 'create';
      const targetId = firstOp?.target_circle_id ?? firstOp?.circle_id ?? tensionDraft.circle_id ?? null;
      const rawParentId = firstOp?.data?.parent_id !== undefined
        ? firstOp.data.parent_id
        : isCreateAction
          ? (firstOp?.circle_id ?? tensionDraft.circle_id ?? null)
          : (tensionDraft.proposed_data?.parent_id ?? null);
      const safeParentId = !isCreateAction && rawParentId !== null && rawParentId !== undefined && targetId !== null && String(rawParentId) === String(targetId)
        ? null
        : rawParentId;
      const proposed_data =
        firstOp?.action === 'delete' || tensionDraft.action === 'delete'
          ? { delete: true }
          : firstOp?.data
            ? {
              name: firstOp.data.name || '',
              purpose: firstOp.data.purpose || '',
              domains: firstOp.data.domains || [],
              accountabilities: firstOp.data.accountabilities || [],
              parent_id: safeParentId,
            }
            : {
              name: tensionDraft.proposed_data?.name || '',
              purpose: tensionDraft.proposed_data?.purpose || '',
              domains: tensionDraft.proposed_data?.domains || [],
              accountabilities: tensionDraft.proposed_data?.accountabilities || [],
              parent_id: safeParentId,
            };

      const payload = {
        circle_id: tensionDraft.circle_id,
        expressed_by_role_id: tensionDraft.expressed_by_role_id ? Number(tensionDraft.expressed_by_role_id) : null,
        action: firstOp?.action || tensionDraft.action || 'edit',
        title: fallbackTitle,
        summary: tensionDraft.summary,
        proposed_data,
        operations,
      };
      const isUpdate = Boolean(tensionDraft.id);
      const url = isUpdate
        ? `${API_URL}/circle-tensions/${tensionDraft.id}`
        : `${API_URL}/circle-tensions`;
      const method = isUpdate ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Impossible d'enregistrer la tension");
      const saved = normalizeTension(await response.json());
      setTensionDraft(saved);
      setSelectedTensionId(saved.id);
      await fetchTensions();
      if (!shouldAutoTitle) {
        setShowTensionModal(false);
      }
      if (shouldAutoTitle) {
        generateTensionTitle(tensionDraft)
          .then(async (suggested) => {
            const nextTitle = normalizeTensionTitle(suggested);
            if (!nextTitle || nextTitle === fallbackTitle) return;
            const titleResponse = await fetch(
              `${API_URL}/circle-tensions/${saved.id}`,
              {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                body: JSON.stringify({ title: nextTitle }),
              },
            );
            if (!titleResponse.ok) return;
            const updated = normalizeTension(await titleResponse.json());
            setTensionDraft(updated);
            await fetchTensions();
          })
          .catch((err) => {
            console.warn('Failed to update tension title:', err);
          });
      }
    } catch (err) {
      setTensionError(err.message);
    } finally {
      setTensionsLoading(false);
    }
  };

  const handleDeleteTension = async (tensionId) => {
    if (!window.confirm("Supprimer cette tension ?")) return;
    try {
      setTensionsLoading(true);
      const response = await fetch(`${API_URL}/circle-tensions/${tensionId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error("Suppression impossible");
      setTensionDraft(null);
      setSelectedTensionId(null);
      await fetchTensions();
    } catch (err) {
      setTensionError(err.message);
    } finally {
      setTensionsLoading(false);
    }
  };

  const handleProposeTension = async (tensionId) => {
    try {
      setTensionsLoading(true);
      const response = await fetch(`${API_URL}/circle-votes/propose/${tensionId}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error("Impossible de lancer le vote");
      await fetchTensions();
      await fetchVotes();
      await fetchTensionVotes(tensionId);
    } catch (err) {
      alert(err.message);
    } finally {
      setTensionsLoading(false);
    }
  };

  const handleVoteDecision = async (voteId, decision) => {
    try {
      setVotesLoading(true);
      const response = await fetch(`${API_URL}/circle-votes/${voteId}/decision?decision=${decision}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error("Vote impossible");
      await fetchVotes();
      await fetchTensions();
      await fetchCircles();
      if (tensionDraft?.id) {
        await fetchTensionVotes(tensionDraft.id);
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setVotesLoading(false);
    }
  };

  const handleWithdrawProposal = async (tensionId) => {
    try {
      setTensionsLoading(true);
      const response = await fetch(`${API_URL}/circle-votes/propose/${tensionId}/withdraw`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error("Impossible de retirer la proposition");
      await fetchTensions();
      await fetchVotes();
      await fetchTensionVotes(tensionId);
    } catch (err) {
      alert(err.message);
    } finally {
      setTensionsLoading(false);
    }
  };


  // Refs for focusing when a field becomes editable
  const nameRef = useRef(null);
  const purposeRef = useRef(null);
  const ownerRef = useRef(null);
  const domainNewRef = useRef(null);
  const accountabilityNewRef = useRef(null);
  const strategyNewRef = useRef(null);

  useEffect(() => {
    if (editingField === 'name') nameRef.current?.focus();
    if (editingField === 'purpose') purposeRef.current?.focus();
    if (editingField === 'owner') ownerRef.current?.focus();
    if (editingField === 'domains') domainNewRef.current?.focus();
    if (editingField === 'accountabilities') accountabilityNewRef.current?.focus();
    if (editingField === 'strategy') strategyNewRef.current?.focus();
  }, [editingField]);

  // Permission Logic
  const userLeadsCircle = useCallback(
    (circle) => {
      if (!effectiveUserId || !circle) return false;
      const userId = Number(effectiveUserId);
      if (Number.isNaN(userId)) return false;
      const owners = [];
      if (circle.owner_id) owners.push(circle.owner_id);
      if (Array.isArray(circle.owner_ids)) owners.push(...circle.owner_ids);
      return owners.some((id) => Number(id) === userId);
    },
    [effectiveUserId]
  );

  const isRoleCircle = useCallback(
    (circle) => {
      if (!circle?.id) return false;
      if (!circle.parent_id) return false;
      return !(childrenByParent[circle.id]?.length);
    },
    [childrenByParent]
  );

  const isLeafRole = React.useMemo(() => {
    if (!selectedCircle?.id) return false;
    if (!selectedCircle.parent_id) return false;
    return !(childrenByParent[selectedCircle.id]?.length);
  }, [selectedCircle, childrenByParent]);


  const directRoles = React.useMemo(() => {
    if (!selectedCircle?.id) return [];
    return (childrenByParent[selectedCircle.id] || []).filter(isRoleCircle);
  }, [selectedCircle, childrenByParent, isRoleCircle]);

  const getDescendantIds = useCallback(
    (circleId) => {
      const ids = new Set();
      const stack = [circleId];
      while (stack.length > 0) {
        const current = stack.pop();
        if (!current || ids.has(current)) continue;
        ids.add(current);
        const children = childrenByParent[current] || [];
        children.forEach((child) => {
          if (child?.id) stack.push(child.id);
        });
      }
      return ids;
    },
    [childrenByParent]
  );

  const canViewSelectedCircleProjects = Boolean(selectedCircle?.id);
  const isInsightsTab = activeTab === 'insights';

  const projectsInScope = React.useMemo(() => {
    if (!selectedCircle?.id) return [];
    const ids = getDescendantIds(selectedCircle.id);
    const list = (projects || []).filter((project) => {
      if (!project?.circle_id) return false;
      return ids.has(Number(project.circle_id));
    });
    return list.sort((a, b) => {
      const left = Number(a.project_number || a.id || 0);
      const right = Number(b.project_number || b.id || 0);
      return left - right;
    });
  }, [selectedCircle, projects, getDescendantIds]);

  useEffect(() => {
    const shouldLoadScopeMetrics = activeTab === 'insights' || activeTab === 'structure';
    if (!shouldLoadScopeMetrics) return;
    if (!projectsInScope.length) return;
    let cancelled = false;
    let idleId = null;
    let timeoutId = null;
    const pendingKeys = new Set();
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    const loadMetrics = async () => {
      const pending = projectsInScope.filter((project) => {
        const key = String(project.id);
        return (
          !projectMetricsByIdRef.current[key] &&
          !projectMetricsLoadingRef.current[key]
        );
      });
      if (pending.length === 0) return;
      setProjectMetricsError(null);
      const loadingPatch = {};
      pending.forEach((project) => {
        const key = String(project.id);
        pendingKeys.add(key);
        loadingPatch[key] = true;
      });
      if (isMountedRef.current && !cancelled) {
        setProjectMetricsLoading((prev) => ({ ...prev, ...loadingPatch }));
      }
      const concurrency = lowPowerDevice ? 2 : 4;
      let cursor = 0;
      const loadedPatch = {};
      let loadError = null;
      const runProject = async (project, attempt = 0) => {
        const key = String(project.id);
        try {
          const data = await fetchProjectMetrics(project.id);
          if (!isMountedRef.current || cancelled) return;
          loadedPatch[key] = data;
        } catch (err) {
          if (!isMountedRef.current || cancelled) return;
          if (attempt < 2) {
            await sleep(300 * (attempt + 1));
            return runProject(project, attempt + 1);
          }
          if (!loadError) {
            loadError = err?.message || 'Erreur lors du chargement des metrics projet.';
          }
        }
      };
      const workers = Array.from(
        { length: Math.min(concurrency, pending.length) },
        async () => {
          while (!cancelled) {
            const project = pending[cursor];
            cursor += 1;
            if (!project) break;
            await runProject(project);
          }
        },
      );
      await Promise.all(workers);
      if (!isMountedRef.current || cancelled) return;
      if (Object.keys(loadedPatch).length > 0) {
        setProjectMetricsById((prev) => ({ ...prev, ...loadedPatch }));
      }
      if (loadError) {
        setProjectMetricsError(loadError);
      }
      setProjectMetricsLoading((prev) => {
        const next = { ...prev };
        pendingKeys.forEach((key) => {
          next[key] = false;
        });
        return next;
      });
    };
    if (activeTab === 'structure') {
      if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
        idleId = window.requestIdleCallback(() => {
          loadMetrics();
        }, { timeout: 1400 });
      } else {
        timeoutId = window.setTimeout(() => {
          loadMetrics();
        }, 250);
      }
    } else {
      loadMetrics();
    }
    return () => {
      cancelled = true;
      if (idleId !== null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      if (!isMountedRef.current || pendingKeys.size === 0) return;
      setProjectMetricsLoading((prev) => {
        const next = { ...prev };
        pendingKeys.forEach((key) => {
          next[key] = false;
        });
        return next;
      });
    };
  }, [activeTab, projectsInScope, fetchProjectMetrics, lowPowerDevice]);

  const metricsRangeInfo = React.useMemo(() => {
    const start = parseDateInput(metricsRange.start);
    const end = parseDateInput(metricsRange.end);
    if (!start || !end) {
      return { start: null, end: null, isValid: false };
    }
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    return { start, end, isValid: start <= end };
  }, [metricsRange, parseDateInput]);

  const projectRangeStatsById = React.useMemo(() => {
    const statsById = {};
    if (!metricsRangeInfo.isValid) return statsById;
    projectsInScope.forEach((project) => {
      const key = String(project.id);
      const metrics = projectMetricsById[key];
      if (!Array.isArray(metrics) || metrics.length === 0) return;
      let hours = 0;
      let expenses = 0;
      let tokens = 0;
      let hasEntry = false;
      metrics.forEach((entry) => {
        const entryDate = parseDateInput(entry.date);
        if (!entryDate) return;
        if (entryDate >= metricsRangeInfo.start && entryDate <= metricsRangeInfo.end) {
          hours += Number(entry.hours_daily || 0);
          expenses += Number(entry.expenses_daily || 0);
          tokens += Number(entry.tokens_daily || 0);
          hasEntry = true;
        }
      });
      statsById[key] = { hours, expenses, tokens, hasEntry };
    });
    return statsById;
  }, [projectsInScope, projectMetricsById, metricsRangeInfo, parseDateInput]);

  const circleMetricsSummary = React.useMemo(() => {
    if (!metricsRangeInfo.isValid) {
      return { hours: 0, expenses: 0, projects: 0, covered: 0, velocity: 0 };
    }
    let hours = 0;
    let expenses = 0;
    let covered = 0;
    let velocitySum = 0;
    let velocityCount = 0;
    projectsInScope.forEach((project) => {
      const stats = projectRangeStatsById[String(project.id)];
      if (!stats || !stats.hasEntry) return;
      covered += 1;
      hours += Number(stats.hours || 0);
      expenses += Number(stats.expenses || 0);
      if (stats.hours > 0) {
        velocitySum += (Number(stats.tokens || 0) * DAILY_HOURS) / Number(stats.hours);
        velocityCount += 1;
      }
    });
    const velocity = velocityCount > 0 ? velocitySum / velocityCount : 0;
    return {
      hours,
      expenses,
      projects: projectsInScope.length,
      covered,
      velocity,
    };
  }, [projectsInScope, projectRangeStatsById, metricsRangeInfo, DAILY_HOURS]);

  const circleMetricsLoading = React.useMemo(() => {
    if (!projectsInScope.length) return false;
    return projectsInScope.some(
      (project) => projectMetricsLoading[String(project.id)],
    );
  }, [projectsInScope, projectMetricsLoading]);

  const projectTotals = React.useMemo(() => {
    if (!isInsightsTab) return [];
    return (projectsInScope || []).map((project) => {
      const stats = projectRangeStatsById[String(project.id)] || {
        hours: 0,
        expenses: 0,
      };
      return {
        project,
        hours: Number(stats.hours || 0),
        expenses: Number(stats.expenses || 0),
      };
    });
  }, [projectsInScope, projectRangeStatsById, isInsightsTab]);

  const projectTotalsById = React.useMemo(() => {
    if (!isInsightsTab) return {};
    const map = {};
    projectTotals.forEach((item) => {
      map[String(item.project.id)] = item;
    });
    return map;
  }, [projectTotals, isInsightsTab]);

  const projectsInScopeByCircle = React.useMemo(() => {
    const map = {};
    projectsInScope.forEach((project) => {
      const key = String(project.circle_id || '');
      if (!map[key]) map[key] = [];
      map[key].push(project);
    });
    return map;
  }, [projectsInScope]);

  const minVisualValue = 0.05;
  const getProjectMetricValue = useCallback(
    (project, metricKey) => {
      const totals = projectTotalsById[String(project.id)] || { hours: 0, expenses: 0 };
      const rawValue = Number(totals[metricKey] || 0);
      if (!Number.isFinite(rawValue)) return minVisualValue;
      return rawValue > 0 ? rawValue : minVisualValue;
    },
    [projectTotalsById]
  );

  const buildInsightHierarchy = useCallback(
    (rootCircle, metricKey) => {
      if (!rootCircle?.id) return null;
      const buildNode = (circle) => {
        const childCircles = (childrenByParent[circle.id] || []).map(buildNode);
        const projectNodes = (projectsInScopeByCircle[String(circle.id)] || [])
          .map((project) => {
            const totals = projectTotalsById[String(project.id)] || { hours: 0, expenses: 0 };
            const rawValue = Number(totals[metricKey] || 0);
            const sizeValue = getProjectMetricValue(project, metricKey);
            return {
              id: `project-${project.id}`,
              name: project.name || `Projet ${project.id}`,
              nodeType: 'project',
              project_id: project.id,
              project_number: project.project_number,
              circle_id: project.circle_id,
              value: sizeValue,
              rawValue,
            };
          });
        const children = [...childCircles, ...projectNodes];
        const rawValue = children.reduce((sum, child) => sum + Number(child.rawValue || 0), 0);
        const baseNode = {
          id: circle.id,
          name: circle.name || `Cercle ${circle.id}`,
          parent_id: circle.parent_id ?? null,
          owner_id: circle.owner_id,
          owner_ids: circle.owner_ids,
          nodeType: 'circle',
        };
        if (children.length > 0) {
          return { ...baseNode, children, rawValue };
        }
        return { ...baseNode, value: minVisualValue, rawValue: 0 };
      };
      return buildNode(rootCircle);
    },
    [childrenByParent, projectsInScopeByCircle, getProjectMetricValue, projectTotalsById]
  );

  const hoursHierarchy = React.useMemo(() => {
    if (!isInsightsTab) return null;
    return buildInsightHierarchy(selectedCircle, 'hours');
  }, [isInsightsTab, buildInsightHierarchy, selectedCircle]);
  const expensesHierarchy = React.useMemo(() => {
    if (!isInsightsTab) return null;
    return buildInsightHierarchy(selectedCircle, 'expenses');
  }, [isInsightsTab, buildInsightHierarchy, selectedCircle]);

  const isUsingSlotRange = React.useMemo(() => {
    if (!currentSlotRange) return false;
    return (
      metricsRange.start === currentSlotRange.start &&
      metricsRange.end === currentSlotRange.end
    );
  }, [metricsRange, currentSlotRange]);

  const mentionItems = React.useMemo(() => {
    const items = [];
    (circles || []).forEach((circle) => {
      if (!circle?.id || !circle?.name) return;
      const type = isRoleCircle(circle) ? 'role' : 'circle';
      items.push({ type, id: circle.id, name: circle.name });
    });
    (projects || []).forEach((project) => {
      if (!project?.id || !project?.name) return;
      items.push({ type: 'project', id: project.id, name: project.name });
    });
    return items;
  }, [circles, projects, isRoleCircle]);

  const findEmployee = useCallback(
    (id) => employees.find((e) => Number(e.id) === Number(id)),
    [employees]
  );

  const activeEmployees = React.useMemo(
    () => employees.filter((emp) => emp?.is_active !== false),
    [employees]
  );


  const canEditLeader = React.useMemo(() => {
    if (superEditMode) return true;
    if (!user || !selectedCircle || !selectedCircle.id) return false;
    const parent = circles.find((c) => c.id === selectedCircle.parent_id);
    return userLeadsCircle(parent);
  }, [superEditMode, user, selectedCircle, circles, userLeadsCircle]);

  const canEditStrategy = React.useMemo(
    () => superEditMode || userLeadsCircle(selectedCircle),
    [superEditMode, userLeadsCircle, selectedCircle]
  );

  const canEditVoteTimeout = React.useMemo(
    () => superEditMode || userLeadsCircle(selectedCircle),
    [superEditMode, userLeadsCircle, selectedCircle]
  );

  const canEditColor = React.useMemo(
    () => superEditMode || userLeadsCircle(selectedCircle),
    [superEditMode, userLeadsCircle, selectedCircle]
  );

  const canAddSubCircle = React.useMemo(() => {
    if (!selectedCircle || !selectedCircle.id) return false;
    return superEditMode || userLeadsCircle(selectedCircle);
  }, [selectedCircle, userLeadsCircle, superEditMode]);

  const canEditRoleFocus = React.useMemo(
    () => {
      if (superEditMode) return true;
      if (!selectedCircle?.id) return false;
      if (isLeafRole) {
        const parent = circles.find((circle) => circle.id === selectedCircle.parent_id);
        return userLeadsCircle(parent);
      }
      return userLeadsCircle(selectedCircle);
    },
    [superEditMode, userLeadsCircle, selectedCircle, circles, isLeafRole]
  );

  if (loading) return <div className="p-8 text-center">Chargement...</div>;
  if (error) return <div className="p-8 text-center text-red-500">Erreur: {error}</div>;

  const isMeeting = activeTab === 'meeting';
  const isWideTab = activeTab === 'tensions' || activeTab === 'insights';
  const isMeetingSecretary = String(activeMeeting?.created_by) === String(effectiveUserId);
  const pendingVotesCount = Array.isArray(votes) ? votes.length : 0;
  const pendingVotesLabel = pendingVotesCount > 9 ? '9+' : String(pendingVotesCount);
  const effectiveMeetingProjectId = isMeetingSecretary
    ? selectedMeetingProjectId
    : (selectedMeetingProjectIdShared ?? selectedMeetingProjectId);
  const meetingHistoryModal = (
    <MeetingHistoryModal
      isOpen={showMeetingHistory}
      onClose={handleCloseMeetingHistory}
      meetingHistory={meetingHistory}
      meetingHistoryLoading={meetingHistoryLoading}
      meetingHistoryError={meetingHistoryError}
      circleName={selectedCircle?.name}
    />
  );
  const renderTabs = () => (
    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-3">
      <button
        onClick={() => setActiveTab('structure')}
        className={`px-3 py-2 rounded-xl text-xs sm:text-sm font-semibold border whitespace-nowrap ${activeTab === 'structure' ? 'bg-white shadow text-slate-900 border-slate-200' : 'bg-slate-100 text-slate-500 border-transparent'}`}
      >
        Structure
      </button>
      <button
        onClick={() => setActiveTab('insights')}
        className={`px-3 py-2 rounded-xl text-xs sm:text-sm font-semibold border flex items-center gap-1.5 sm:gap-2 whitespace-nowrap ${activeTab === 'insights' ? 'bg-white shadow text-slate-900 border-slate-200' : 'bg-slate-100 text-slate-500 border-transparent'}`}
      >
        <BarChart3 size={16} />
        Insights
      </button>
      <button
        onClick={() => setActiveTab('tensions')}
        className={`px-3 py-2 rounded-xl text-xs sm:text-sm font-semibold border flex items-center gap-1.5 sm:gap-2 whitespace-nowrap ${activeTab === 'tensions' ? 'bg-white shadow text-slate-900 border-slate-200' : 'bg-slate-100 text-slate-500 border-transparent'}`}
      >
        <GitMerge size={16} />
        Tensions
      </button>
      <button
        onClick={() => setActiveTab('points')}
        className={`px-3 py-2 rounded-xl text-xs sm:text-sm font-semibold border flex items-center gap-1.5 sm:gap-2 whitespace-nowrap ${activeTab === 'points' ? 'bg-white shadow text-slate-900 border-slate-200' : 'bg-slate-100 text-slate-500 border-transparent'}`}
      >
        <ListChecks size={16} />
        Points
      </button>
      <button
        onClick={() => setActiveTab('votes')}
        className={`px-3 py-2 rounded-xl text-xs sm:text-sm font-semibold border flex items-center gap-1.5 sm:gap-2 whitespace-nowrap ${activeTab === 'votes' ? 'bg-white shadow text-slate-900 border-slate-200' : 'bg-slate-100 text-slate-500 border-transparent'}`}
      >
        Votes
        {pendingVotesCount > 0 && (
          <span className="inline-flex items-center justify-center rounded-full bg-rose-500 px-2 py-0.5 text-[10px] font-semibold text-white">
            {pendingVotesLabel}
          </span>
        )}
      </button>
      {activeMeeting && (
        <button
          onClick={() => setActiveTab('meeting')}
          className={`px-3 py-2 rounded-xl text-xs sm:text-sm font-semibold border flex items-center gap-1.5 sm:gap-2 whitespace-nowrap ${activeTab === 'meeting' ? 'bg-white shadow text-slate-900 border-slate-200' : 'bg-slate-100 text-slate-500 border-transparent'}`}
        >
          Reunion
        </button>
      )}
      {activeTab === 'tensions' && (
        <button
          onClick={fetchTensions}
          className="ml-auto p-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600"
          title="Actualiser mes tensions"
        >
          <RefreshCcw size={14} />
        </button>
      )}
    </div>
  );

  const renderStructureCard = (compact = false) => {
    const emptyState = (
      <div className="flex flex-col items-center justify-center h-full text-slate-400">
        <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-4">
          <Plus size={28} className="text-slate-300" />
        </div>
        <p className="mb-4 text-sm font-medium text-center">Aucune structure definie</p>
        <button
          onClick={handleInitializeRoot}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg shadow-md hover:bg-indigo-700 transition-colors text-sm"
        >
          Initialiser le cercle d'Ancrage
        </button>
      </div>
    );
    if (!hierarchyData) {
      return (
        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 h-full flex items-center justify-center text-slate-400">
          {circles.length === 0 ? emptyState : 'Aucune structure'}
        </div>
      );
    }
    const circleClick = compact && isMeeting ? () => { } : handleCircleClick;
    const sizeStyle = compact
      ? {
        background: 'radial-gradient(circle at 35% 35%, #eef2ff 0, #ffffff 70%)',
        width: 'clamp(260px, 22vw, 420px)',
        height: 'clamp(260px, 22vw, 420px)',
      }
      : {};
    return (
      <div
        className={`bg-gradient-to-br from-white to-slate-50 rounded-2xl shadow-xl border border-slate-100 ${compact ? 'flex-shrink-0' : 'h-full'
          } flex flex-col overflow-hidden relative`}
        style={sizeStyle}
      >
        <div className={`${compact ? 'flex-1 w-full h-full p-4 flex items-center justify-center' : 'flex-1 w-full h-full'}`}>
          {circles.length === 0 ? (
            emptyState
          ) : (
            <div
              className="w-full h-full flex items-center justify-center overflow-hidden"
              style={{
                transformOrigin: 'center',
                transform: compact ? 'scale(1)' : 'scale(1)',
                pointerEvents: compact && isMeeting ? 'none' : 'auto',
              }}
            >
              <div style={{ width: '96%', height: '96%' }}>
                <CirclePack
                  data={hierarchyData}
                  onCircleClick={circleClick}
                  openMeetingCircleIds={openMeetingCircleIds}
                  selectedCircleId={selectedCircle?.id}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (isMeeting) {
    return (
      <div className="min-h-[calc(100vh-64px)] h-auto overflow-y-auto bg-gray-100">
        <div className="h-auto flex flex-col px-6 pt-2 pb-6">
          {renderTabs()}
          <div className="flex-1 border border-slate-100 bg-white rounded-2xl shadow-sm p-4 overflow-visible">
            <MeetingPanel
              selectedCircle={selectedCircle}
              activeMeeting={activeMeeting}
              meetingError={meetingError}
              meetingParticipants={meetingParticipants}
              meetingDraft={meetingDraft}
              meetingTensions={meetingTensions}
              meetingPoints={meetingPoints}
              meetingPreparedPoints={meetingPreparedPoints}
              meetingPreparedPointsLoading={meetingPreparedPointsLoading}
              selectedMeetingPointId={isMeetingSecretary ? selectedMeetingPointId : (selectedMeetingPointIdShared ?? selectedMeetingPointId)}
              selectedMeetingProjectId={effectiveMeetingProjectId}
              meetingProjectReviews={meetingProjectReviews}
              meetingPointComposer={meetingPointComposer}
              selectedMeetingTensionId={isMeetingSecretary ? selectedMeetingTensionId : (selectedMeetingTensionIdShared ?? selectedMeetingTensionId)}
              meetingNotes={meetingNotes}
              meetingPhase={meetingPhase}
              presenceCount={meetingPresenceCount}
              connectedUsers={meetingConnectedUsers}
              realtimeConnected={meetingRealtimeConnected}
              meetingFormVisible={meetingFormVisible}
              tensions={tensions}
              circles={circles}
              employees={employees}
              projects={projects}
              projectSprints={projectSprints}
              projectMetricsById={projectMetricsById}
              projectMetricsLoading={projectMetricsLoading}
              projectMetricsError={projectMetricsError}
              onFetchProjectSprints={fetchProjectSprints}
              user={user}
              meetingLoading={meetingLoading}
              selectedCircleIsRole={isLeafRole}
              onCloseMeeting={handleCloseMeeting}
              onImportTension={handleImportMeetingTension}
              onMeetingDraftChange={handleMeetingDraftChange}
              onCreateMeetingTension={handleCreateMeetingTension}
              onRefreshMeetingTensions={fetchMeetingTensions}
              onSelectMeetingTension={(id) => {
                if (String(activeMeeting?.created_by) !== String(effectiveUserId)) return;
                setSelectedMeetingTensionId(id);
                sendMeetingRealtime({ type: 'select_tension', tension_id: id });
              }}
              onLiveEditTension={handleLiveMeetingTensionEdit}
              onMeetingDecision={handleMeetingDecision}
              onAddMeetingPoint={handleAddMeetingPoint}
              onImportMeetingPoint={handleImportMeetingPoint}
              onSelectMeetingPoint={handleSelectMeetingPoint}
              onPointDecision={handleMeetingPointDecision}
              onPointToTension={handleMeetingPointToTension}
              onPointNotesChange={handleMeetingPointNotesChange}
              onPointComposerUpdate={handleMeetingPointComposerUpdate}
              onCreatePointTask={handleCreateTacticalTask}
              onCreatePointObjective={handleCreateTacticalObjective}
              onSelectMeetingProject={handleSelectMeetingProject}
              onSelectMeetingPhase={handleSelectMeetingPhase}
              onFetchProjectReviews={fetchMeetingProjectReviews}
              onUpdateProjectReview={handleUpdateMeetingProjectReview}
              onFetchProjectMetrics={fetchProjectMetrics}
              onFetchProjectObjectives={fetchProjectObjectives}
              onNotesChange={handleMeetingNotesChange}
              onToggleForm={handleToggleMeetingForm}
              onTransferSecretary={handleTransferSecretary}
              onInviteParticipant={handleInviteMeetingParticipant}
              metricsRange={metricsRange}
              metricsRangeValid={metricsRangeInfo.isValid}
            />
          </div>
        </div>
        {showTensionModal && tensionDraft && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto relative">
              <button
                className="absolute top-3 right-3 rounded-full p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                onClick={handleCancelTension}
                aria-label="Fermer"
              >
                &#10005;
              </button>
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-700">Edition de tension</p>
                  <span className="text-xs text-slate-500">{selectedCircle?.name || tensionDraft.original_data?.name || 'Cercle'}</span>
                </div>
                <TensionEditor
                  tensionDraft={tensionDraft}
                  circles={circles}
                  user={user}
                  tensionVotes={tensionVotes}
                  tensionVotesLoading={tensionVotesLoading}
                  tensionsLoading={tensionsLoading}
                  isFieldChanged={isTensionFieldChanged}
                  onUpdateField={updateTensionField}
                  onUpdateProposedField={updateTensionProposedField}
                  onUpdateList={updateTensionList}
                  onChangeAction={handleTensionActionChange}
                  onDelete={handleDeleteTension}
                  onPropose={handleProposeTension}
                  onWithdraw={handleWithdrawProposal}
                  onCancel={handleCancelTension}
                  onSave={handleSaveTension}
                  defaultCircleId={selectedCircle?.id}
                />
              </div>
            </div>
          </div>
        )}
        {meetingHistoryModal}
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-64px)] bg-gray-100 overflow-hidden">
      <div
        className={`${isWideTab ? 'hidden' : 'w-1/2 lg:w-3/5 xl:w-2/3'} transition-all duration-500 ease-in-out h-full p-4`}
      >
        {renderStructureCard(false)}
      </div>

      <div
        className={`${isWideTab ? 'w-full' : 'w-1/2 lg:w-2/5 xl:w-1/3'} h-full p-4 pl-0 transition-all duration-300 flex flex-col`}
      >
        {renderTabs()}

        {activeTab === 'structure' ? (
          <StructurePanel
            isEditing={isEditing}
            selectedCircle={selectedCircle}
            employees={employees}
            parentId={selectedCircle ? selectedCircle.parent_id : newCircleParentId}
            isRole={selectedCircle ? (!!selectedCircle.parent_id && !(childrenByParent[selectedCircle.id]?.length)) : Boolean(newCircleParentId)}
            editingField={editingField}
            setEditingField={setEditingField}
            inlineDraft={inlineDraft}
            onUpdateInlineField={updateInlineField}
            onCommitInline={commitInline}
            onInlineCancel={handleInlineCancel}
            canAddSubCircle={canAddSubCircle}
            circleMemberIds={circleMemberIds}
            canEditLeader={canEditLeader}
            canEditStrategy={canEditStrategy}
            canEditVoteTimeout={canEditVoteTimeout}
            canEditColor={canEditColor}
            isLeafRole={isLeafRole}
            activeEmployees={activeEmployees}
            findEmployee={findEmployee}
            onAddSubCircle={handleAddSubCircle}
            onStartTension={handleStartTension}
            onOpenMeeting={handleOpenMeeting}
            onJoinMeeting={handleJoinMeeting}
            onOpenMeetingHistory={handleOpenMeetingHistory}
            meetingLoading={meetingLoading}
            openMeetingsByCircle={openMeetingsByCircle}
            onSave={handleSave}
            onDelete={handleDelete}
            onCancelEdit={handleCancelEdit}
            isSuperEditor={isSuperEditor}
            superEditMode={superEditMode}
            onToggleSuperEdit={handleToggleSuperEdit}
            onStartSuperEdit={handleStartSuperEdit}
            onConsolidateRoots={handleConsolidateRoots}
            onUpdateInlineListItem={updateInlineListItem}
            onRemoveInlineItem={removeInlineItem}
            onAddInlineItem={addInlineItem}
            onUpdateInlineStrategyItem={updateInlineStrategyItem}
            onAddInlineStrategyItem={addInlineStrategyItem}
            onRemoveInlineStrategyItem={removeInlineStrategyItem}
            nameRef={nameRef}
            purposeRef={purposeRef}
            ownerRef={ownerRef}
            domainNewRef={domainNewRef}
            accountabilityNewRef={accountabilityNewRef}
            strategyNewRef={strategyNewRef}
            directRoles={directRoles}
            roleFocusMap={roleFocusMap}
            canEditRoleFocus={canEditRoleFocus}
            onSaveRoleFocus={handleSaveRoleFocus}
            mentionItems={mentionItems}
            circles={circles}
            projects={projects}
            onSelectCircle={handleCircleClick}
            selectedEmployeeId={selectedEmployeeId}
            onSelectEmployee={handleSelectEmployee}
            onClearEmployee={() => setSelectedEmployeeId(null)}
            onOpenProject={(projectId) => navigate(`/project/${projectId}`)}
            projectsInScope={projectsInScope}
            canViewProjects={canViewSelectedCircleProjects}
            onExportOrganization={handleExportOrganization}
            organizationExporting={organizationExporting}
            metricsRange={metricsRange}
            metricsRangeValid={metricsRangeInfo.isValid}
            onMetricsRangeChange={setMetricsRange}
            currentSlotRange={currentSlotRange}
            isUsingSlotRange={isUsingSlotRange}
            circleMetricsSummary={circleMetricsSummary}
            circleMetricsLoading={circleMetricsLoading}
            circleMetricsError={projectMetricsError}
          />
        ) : activeTab === 'insights' ? (
          <div className="flex-1 min-h-0 overflow-y-auto">
            <CircleInsightsPanel
              selectedCircle={selectedCircle}
              metricsRange={metricsRange}
              metricsRangeValid={metricsRangeInfo.isValid}
              onMetricsRangeChange={setMetricsRange}
              currentSlotRange={currentSlotRange}
              isUsingSlotRange={isUsingSlotRange}
              summary={circleMetricsSummary}
              loading={circleMetricsLoading}
              error={projectMetricsError}
              hoursHierarchy={hoursHierarchy}
              expensesHierarchy={expensesHierarchy}
            />
          </div>
        ) : activeTab === 'points' ? (
          <PointsPanel
            selectedCircle={selectedCircle}
            points={circlePoints}
            pointsLoading={circlePointsLoading}
            pointsError={circlePointsError}
            onCreatePoint={handleCreateCirclePoint}
            onDeletePoint={handleDeleteCirclePoint}
            selectedCircleIsRole={isLeafRole}
          />
        ) : activeTab === 'tensions' ? (
          <TensionsPanel
            selectedCircle={selectedCircle}
            tensions={tensions}
            tensionsLoading={tensionsLoading}
            tensionError={tensionError}
            selectedTensionId={selectedTensionId}
            circles={circles}
            tensionDraft={tensionDraft}
            tensionVotes={tensionVotes}
            tensionVotesLoading={tensionVotesLoading}
            onStartTension={handleStartTension}
            onSelectTension={handleSelectTension}
            isFieldChanged={isTensionFieldChanged}
            onUpdateField={updateTensionField}
            onUpdateProposedField={updateTensionProposedField}
            onUpdateList={updateTensionList}
            onChangeAction={handleTensionActionChange}
            onDelete={handleDeleteTension}
            onPropose={handleProposeTension}
            onWithdraw={handleWithdrawProposal}
            onCancel={handleCancelTension}
            onSave={handleSaveTension}
            user={user}
            selectedCircleIsRole={isLeafRole}
          />
        ) : activeTab === 'meeting' ? (
          <MeetingPanel
            selectedCircle={selectedCircle}
            activeMeeting={activeMeeting}
            meetingError={meetingError}
            meetingParticipants={meetingParticipants}
            meetingDraft={meetingDraft}
            meetingTensions={meetingTensions}
            meetingPoints={meetingPoints}
            meetingPreparedPoints={meetingPreparedPoints}
            meetingPreparedPointsLoading={meetingPreparedPointsLoading}
            selectedMeetingPointId={isMeetingSecretary ? selectedMeetingPointId : (selectedMeetingPointIdShared ?? selectedMeetingPointId)}
            selectedMeetingProjectId={effectiveMeetingProjectId}
            meetingProjectReviews={meetingProjectReviews}
            meetingPointComposer={meetingPointComposer}
            selectedMeetingTensionId={isMeetingSecretary ? selectedMeetingTensionId : (selectedMeetingTensionIdShared ?? selectedMeetingTensionId)}
            meetingNotes={meetingNotes}
            meetingPhase={meetingPhase}
            presenceCount={meetingPresenceCount}
            connectedUsers={meetingConnectedUsers}
            realtimeConnected={meetingRealtimeConnected}
            tensions={tensions}
            circles={circles}
            employees={employees}
            projects={projects}
            projectSprints={projectSprints}
            projectMetricsById={projectMetricsById}
            projectMetricsLoading={projectMetricsLoading}
            projectMetricsError={projectMetricsError}
            onFetchProjectSprints={fetchProjectSprints}
            user={user}
            meetingLoading={meetingLoading}
            selectedCircleIsRole={isLeafRole}
            onCloseMeeting={handleCloseMeeting}
            onImportTension={handleImportMeetingTension}
            onMeetingDraftChange={handleMeetingDraftChange}
            onCreateMeetingTension={handleCreateMeetingTension}
            onRefreshMeetingTensions={fetchMeetingTensions}
            onSelectMeetingTension={(id) => {
              if (String(activeMeeting?.created_by) !== String(effectiveUserId)) return;
              setSelectedMeetingTensionId(id);
              sendMeetingRealtime({ type: 'select_tension', tension_id: id });
            }}
            onLiveEditTension={handleLiveMeetingTensionEdit}
            onMeetingDecision={handleMeetingDecision}
            onAddMeetingPoint={handleAddMeetingPoint}
            onImportMeetingPoint={handleImportMeetingPoint}
            onSelectMeetingPoint={handleSelectMeetingPoint}
            onPointDecision={handleMeetingPointDecision}
            onPointToTension={handleMeetingPointToTension}
            onPointNotesChange={handleMeetingPointNotesChange}
            onPointComposerUpdate={handleMeetingPointComposerUpdate}
            onCreatePointTask={handleCreateTacticalTask}
            onCreatePointObjective={handleCreateTacticalObjective}
            onSelectMeetingProject={handleSelectMeetingProject}
            onSelectMeetingPhase={handleSelectMeetingPhase}
            onFetchProjectReviews={fetchMeetingProjectReviews}
            onUpdateProjectReview={handleUpdateMeetingProjectReview}
            onFetchProjectMetrics={fetchProjectMetrics}
            onFetchProjectObjectives={fetchProjectObjectives}
            onNotesChange={handleMeetingNotesChange}
            onToggleForm={handleToggleMeetingForm}
            onTransferSecretary={handleTransferSecretary}
            onInviteParticipant={handleInviteMeetingParticipant}
            metricsRange={metricsRange}
            metricsRangeValid={metricsRangeInfo.isValid}
          />
        ) : (
          <VotesPanel
            votes={votes}
            votesLoading={votesLoading}
            votesError={votesError}
            circles={circles}
            onRefreshVotes={fetchVotes}
            onDecision={handleVoteDecision}
          />
        )}
      </div>

      {showTensionModal && tensionDraft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto relative">
            <button
              className="absolute top-3 right-3 rounded-full p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100"
              onClick={handleCancelTension}
              aria-label="Fermer"
            >
              &#10005;
            </button>
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-700">Edition de tension</p>
                <span className="text-xs text-slate-500">{selectedCircle?.name || tensionDraft.original_data?.name || 'Cercle'}</span>
              </div>
              <TensionEditor
                tensionDraft={tensionDraft}
                circles={circles}
                user={user}
                tensionVotes={tensionVotes}
                tensionVotesLoading={tensionVotesLoading}
                tensionsLoading={tensionsLoading}
                isFieldChanged={isTensionFieldChanged}
                onUpdateField={updateTensionField}
                onUpdateProposedField={updateTensionProposedField}
                onUpdateList={updateTensionList}
                onChangeAction={handleTensionActionChange}
                onDelete={handleDeleteTension}
                onPropose={handleProposeTension}
                onWithdraw={handleWithdrawProposal}
                onCancel={handleCancelTension}
                onSave={handleSaveTension}
                defaultCircleId={selectedCircle?.id}
              />
            </div>
          </div>
        </div>
      )}
      {meetingHistoryModal}
    </div>
  );
};

export default HolacracyPage;
